// src/components/sections/OpportunityContactSection.jsx
import React from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Typography,
  CircularProgress,
  Button
} from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import PersonIcon from '@mui/icons-material/Person';

/**
 * OpportunityContactSection component displays the primary contact information
 * for an opportunity's client, including Revi score.
 */
const OpportunityContactSection = ({ 
  primaryContact, 
  contacts, 
  clientName, 
  opportunityName,
  opportunityId, 
  isLoading, 
  error,
  onViewAllClick,
  reviScore
}) => {
  // Render loading state
  if (isLoading) {
    return (
      <Box sx={{ px: 1.5, pb: 1.5 }}>
        <Typography 
          variant="subtitle2" 
          sx={{ 
            mb: 1, 
            fontSize: '0.75rem', 
            fontWeight: 'bold',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
        >
          <span>PRIMARY CONTACT</span>
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
          <CircularProgress size={20} />
        </Box>
      </Box>
    );
  }

  // Render error state
  if (error || !primaryContact) {
    return (
      <Box sx={{ px: 1.5, pb: 1.5 }}>
        <Typography 
          variant="subtitle2" 
          sx={{ 
            mb: 1, 
            fontSize: '0.75rem', 
            fontWeight: 'bold',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
        >
          <span>PRIMARY CONTACT</span>
        </Typography>
        
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography 
            variant="body2" 
            sx={{ 
              fontSize: '0.75rem', 
              color: 'warning.main' 
            }}
          >
            Could not load contact information
          </Typography>
        </Box>
      </Box>
    );
  }

  const hasMultipleContacts = contacts && contacts.length > 1;

  return (
    <Box sx={{ px: 1.5, pb: 1.5 }}>
      <Typography 
        variant="subtitle2" 
        sx={{ 
          mb: 1, 
          fontSize: '0.75rem', 
          fontWeight: 'bold',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}
      >
        <span>PRIMARY CONTACT</span>
        {hasMultipleContacts && (
          <Button
            variant="text"
            size="small"
            onClick={onViewAllClick}
            sx={{ 
              fontSize: '0.65rem', 
              p: 0, 
              minWidth: 'auto',
              color: '#9B6DFF',
              fontWeight: 'medium',
              '&:hover': {
                backgroundColor: 'transparent',
                textDecoration: 'underline'
              }
            }}
          >
            VIEW ALL
          </Button>
        )}
      </Typography>
      
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PersonIcon sx={{ fontSize: '0.9rem', color: 'primary.main' }} />
          <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
            {primaryContact.name || 
             primaryContact.full_name || 
             (primaryContact.first_name && primaryContact.last_name ? 
               `${primaryContact.first_name} ${primaryContact.last_name}` : 
               primaryContact.title || 'Unknown Contact')}
            {primaryContact.title ? ` (${primaryContact.title})` : ''}
          </Typography>
        </Box>
        
        {primaryContact.email && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <EmailIcon 
              sx={{ 
                fontSize: '0.9rem', 
                color: 'primary.main',
                cursor: 'pointer',
                '&:hover': { color: '#64B5F6' }
              }} 
              onClick={() => window.location.href = `mailto:${primaryContact.email}`}
            />
            <Typography 
              variant="body2" 
              sx={{ 
                fontSize: '0.75rem',
                cursor: 'pointer',
                '&:hover': { color: '#64B5F6' }
              }}
              onClick={() => window.location.href = `mailto:${primaryContact.email}`}
            >
              {primaryContact.email}
            </Typography>
          </Box>
        )}
        
        {primaryContact.phone && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PhoneIcon 
              sx={{ 
                fontSize: '0.9rem', 
                color: 'primary.main',
                cursor: 'pointer',
                '&:hover': { color: '#03DAC6' }
              }} 
              onClick={() => window.location.href = `tel:${primaryContact.phone}`}
            />
            <Typography 
              variant="body2" 
              sx={{ 
                fontSize: '0.75rem',
                cursor: 'pointer',
                '&:hover': { color: '#03DAC6' }
              }}
              onClick={() => window.location.href = `tel:${primaryContact.phone}`}
            >
              {primaryContact.phone}
            </Typography>
          </Box>
        )}
        
        {/* Removed Revi Score section as it's already shown in the header */}
      </Box>
    </Box>
  );
};

OpportunityContactSection.propTypes = {
  primaryContact: PropTypes.object,
  contacts: PropTypes.array,
  clientName: PropTypes.string,
  opportunityName: PropTypes.string,
  opportunityId: PropTypes.string,
  isLoading: PropTypes.bool,
  error: PropTypes.string,
  onViewAllClick: PropTypes.func,
  reviScore: PropTypes.number
};

export default OpportunityContactSection;
